import {
    formatPercentString,
    maybeUnconstrainedThresholdLabel
} from "components/common/constraintFormattingHelpers";
import { getResourceTypeLabel, LabelStyleType } from "components/common/labels";
import { Constraint } from "types/constraintType";
import { LOAD_TYPE } from "types/generatorType";
import { LineTapConstraint } from "types/lineConstraintType";
import { Loading } from "types/loadingType";
import {
    RelatedProjectsByConstraint,
    relatedProjectsConstraintKey
} from "types/queueProjectRelatednessType";
import { downloadJSONToCSV } from "utils/csv";
import { PowerAmounts } from "../BusDetailPane";
import {
    getAllocatedCostForConstraint,
    getImpactAsPercentOfRating,
    getInjectionSizeForConstraint,
    isConstraintWithinSize
} from "../constraintsHelpers";
import { ConstraintTableConfig } from "./ConstraintsDetailsDialogTable";
/**
 * All the information that can be used to render a cell value in the constraint table.
 */

export interface RenderConstraintTableValueParams
    extends RenderConstraintTableValueConfig {
    constraint: Constraint | LineTapConstraint;
    withinSize: boolean;
    // Per-constraint related queue projects, keyed by
    // relatedProjectsConstraintKey. Backs the "Related projects" count
    // column; absent when the table has no relatedness data (the column
    // is hidden then, so cells never render).
    relatedProjectsByConstraint?: RelatedProjectsByConstraint;
}
export interface RenderConstraintTableValueConfig {
    powerAmounts: PowerAmounts;
    labelStyle: LabelStyleType;
    constraintTableConfig: ConstraintTableConfig;
}
/**
 * Represents a single column in the constraint table, including its header label,
 * how it should be rendered, and whether it should be hidden based on the config.
 */
interface ConstraintTableColumn {
    getHeader: (config: ConstraintTableConfig) => string;
    getExportHeader?: (config: ConstraintTableConfig) => string;
    renderValue: (params: RenderConstraintTableValueParams) => string;
    renderExportValue?: (params: RenderConstraintTableValueParams) => string;
    hideColumn?: (config: ConstraintTableConfig) => boolean;
    ellipsizedColumn: boolean;
    // Detail column dropped from the table when `config.compactColumns`
    // is set; `getCompactHiddenColumns` surfaces these in the row's
    // expand panel instead (bus page).
    hideWhenCompact?: boolean;
}
export interface ConfiguredConstraintTableColumn extends ConstraintTableColumn {
    header: string;
}
export const CONSTRAINT_TABLE_COLUMNS: ConstraintTableColumn[] = [
    {
        getHeader: () => "Monitored facility",
        renderValue: ({ constraint }) => constraint.monitoredFacilityName,
        ellipsizedColumn: false
    },
    {
        getHeader: () => "Allocated cost",
        getExportHeader: () => "Allocated cost ($M)",
        renderValue: ({ constraint, powerAmounts, withinSize }) =>
            withinSize
                ? formatCostString(
                      getAllocatedCostForConstraint(constraint, powerAmounts)
                  )
                : "-",
        renderExportValue: ({ constraint, powerAmounts, withinSize }) =>
            withinSize
                ? formatCostStringForExport(
                      getAllocatedCostForConstraint(constraint, powerAmounts)
                  )
                : "-",
        hideColumn: (config) => config.hideAllocatedCosts,
        ellipsizedColumn: false
    },
    {
        getHeader: () => "Estimated cost",
        getExportHeader: () => "Estimated cost ($M)",
        renderValue: ({ constraint }) =>
            formatCostString(constraint.estimatedCost),
        renderExportValue: ({ constraint }) =>
            formatCostStringForExport(constraint.estimatedCost),
        hideColumn: (config) => config.hideTotalCosts,
        ellipsizedColumn: false
    },
    {
        getHeader: () => "Model",
        renderValue: ({ constraint, constraintTableConfig }) =>
            constraintTableConfig.maybeGetModelName !== undefined
                ? constraintTableConfig.maybeGetModelName(constraint)
                : "",
        hideColumn: (config) => config.maybeGetModelName === undefined,
        ellipsizedColumn: false
    },
    {
        getHeader: (config) =>
            config.generatorType === LOAD_TYPE
                ? "Threshold"
                : "Injection threshold",
        getExportHeader: (config) =>
            config.generatorType === LOAD_TYPE
                ? "Threshold (MW)"
                : "Injection threshold (MW)",
        renderValue: ({ constraint }) =>
            maybeUnconstrainedThresholdLabel(constraint.injectionThreshold) ??
            `${constraint.injectionThreshold.toFixed(1)} MW`,
        renderExportValue: ({ constraint }) =>
            maybeUnconstrainedThresholdLabel(constraint.injectionThreshold) ===
            undefined
                ? constraint.injectionThreshold.toFixed(1)
                : "",
        ellipsizedColumn: false
    },
    {
        getHeader: () => "Resource type",
        renderValue: ({ constraint, labelStyle }) =>
            getResourceTypeLabel(constraint.resourceType, labelStyle),
        ellipsizedColumn: false,
        hideColumn: (config) =>
            config.generatorType === LOAD_TYPE || config.hideResourceType,
        hideWhenCompact: true
    },
    {
        getHeader: () => "Existing load",
        renderValue: ({ constraint }) =>
            formatPercentString(constraint.preStudyLoad),
        ellipsizedColumn: false,
        hideColumn: (config) => config.missingBranchMetadata
    },
    {
        getHeader: () => "New load",
        renderValue: ({ constraint, powerAmounts }) =>
            formatPercentString(getPostStudyLoad(constraint, powerAmounts)),
        hideColumn: (config) => !config.showPostStudyLoad,
        ellipsizedColumn: false
    },
    {
        getHeader: () => "Element type",
        renderValue: ({ constraint }) => constraint.elementType,
        ellipsizedColumn: false,
        hideWhenCompact: true
    },
    {
        getHeader: () => "Line length",
        getExportHeader: () => "Line length (miles)",
        renderValue: ({ constraint }) =>
            constraint.lineLength === undefined
                ? "-"
                : `${constraint.lineLength.toFixed(1)} miles`,
        renderExportValue: ({ constraint }) =>
            constraint.lineLength === undefined
                ? "-"
                : constraint.lineLength.toFixed(1),
        hideColumn: (config) => config.missingBranchMetadata,
        ellipsizedColumn: false,
        hideWhenCompact: true
    },
    {
        getHeader: () => "Dfax",
        renderValue: ({ constraint }) =>
            `${(constraint.dfax * 100).toFixed(2)}%`,
        hideColumn: (config) => config.missingBranchMetadata,
        ellipsizedColumn: false
    },
    {
        getHeader: () => "Impact (% of MVA)",
        renderValue: ({ constraint, powerAmounts }) =>
            formatImpactPercent(
                getImpactAsPercentOfRating(constraint, powerAmounts)
            ),
        renderExportValue: ({ constraint, powerAmounts }) =>
            (
                getImpactAsPercentOfRating(constraint, powerAmounts) * 100
            ).toFixed(2),
        hideColumn: (config) =>
            !config.showImpactPercent || config.missingBranchMetadata,
        ellipsizedColumn: false,
        hideWhenCompact: true
    },
    {
        getHeader: () => "Rating (MVA)",
        renderValue: ({ constraint }) => constraint.rating.toFixed(0),
        hideColumn: (config) => config.missingBranchMetadata,
        ellipsizedColumn: false,
        hideWhenCompact: true
    },
    {
        // "Contingency Name" only when the ERCOT-only detail columns are shown;
        // other regions keep the original single "Contingency" column.
        getHeader: (config) =>
            config.showContingencyDetails ? "Contingency Name" : "Contingency",
        renderValue: ({ constraint }) => constraint.contingency,
        hideColumn: (config) => !config.showContingency,
        ellipsizedColumn: true,
        hideWhenCompact: true
    },
    {
        getHeader: () => "Contingency Type",
        renderValue: ({ constraint }) =>
            "contingencyType" in constraint
                ? constraint.contingencyType ?? "-"
                : "-",
        hideColumn: (config) => !config.showContingencyDetails,
        ellipsizedColumn: true,
        hideWhenCompact: true
    },
    {
        getHeader: () => "Contingency Definition",
        renderValue: ({ constraint }) =>
            "contingencyBuses" in constraint
                ? constraint.contingencyBuses ?? "-"
                : "-",
        hideColumn: (config) => !config.showContingencyDetails,
        ellipsizedColumn: true,
        hideWhenCompact: true
    },
    {
        // Count of related queue projects on the constraint, so users can
        // see which constraints are worth drilling into before expanding
        // the row. Only shown when the table has relatedness data (see
        // showRelatedProjectsCount).
        getHeader: () => "Related projects",
        renderValue: ({ constraint, relatedProjectsByConstraint }) => {
            const projects = relatedProjectsByConstraint?.get(
                relatedProjectsConstraintKey(
                    constraint.monitoredFacilityId,
                    constraint.contingency,
                    constraint.resourceType
                )
            );
            return `${projects?.length ?? 0}`;
        },
        hideColumn: (config) => config.showRelatedProjectsCount !== true,
        ellipsizedColumn: false
    }
];
/**
 * Given a ConstraintTableConfig and list of columns, filters down the list of columns
 * to only those that should be shown based on the config.
 */
export const getConfiguredColumns = (
    columns: ConstraintTableColumn[],
    config: ConstraintTableConfig
): ConfiguredConstraintTableColumn[] => {
    return columns
        .filter(
            (column) =>
                !isColumnHiddenByConfig(column, config) &&
                !isColumnHiddenByCompactMode(column, config)
        )
        .map((column) => {
            return {
                ...column,
                header: column.getHeader(config)
            };
        });
};

const isColumnHiddenByConfig = (
    column: ConstraintTableColumn,
    config: ConstraintTableConfig
): boolean => column.hideColumn !== undefined && column.hideColumn(config);

const isColumnHiddenByCompactMode = (
    column: ConstraintTableColumn,
    config: ConstraintTableConfig
): boolean => config.compactColumns === true && column.hideWhenCompact === true;

/**
 * The columns hidden from the table solely because of `compactColumns` —
 * i.e. the detail columns this config would otherwise show. The bus page
 * renders these inside each row's expand panel so the data stays one
 * click away rather than gone.
 */
export const getCompactHiddenColumns = (
    config: ConstraintTableConfig
): ConfiguredConstraintTableColumn[] => {
    if (config.compactColumns !== true) {
        return [];
    }
    return CONSTRAINT_TABLE_COLUMNS.filter(
        (column) =>
            isColumnHiddenByCompactMode(column, config) &&
            !isColumnHiddenByConfig(column, config)
    ).map((column) => ({
        ...column,
        header: column.getHeader(config)
    }));
};

// Impact MW capped. Renders as ">100%".
const IMPACT_PERCENT_DISPLAY_CAP = 1;

const formatImpactPercent = (impactAsPercentOfRating: number): string => {
    if (impactAsPercentOfRating > IMPACT_PERCENT_DISPLAY_CAP) {
        return `>${formatPercentString(IMPACT_PERCENT_DISPLAY_CAP)}`;
    }
    return formatPercentString(impactAsPercentOfRating, 2);
};

const formatCostString = (cost: number | undefined): string => {
    if (cost === undefined) {
        return "N/A";
    }
    return `${`$${(cost / 1000000).toFixed(1)}M`}`;
};
const formatCostStringForExport = (cost: number | undefined): string => {
    if (cost === undefined) {
        return "N/A";
    }
    return (cost / 1000000).toFixed(1);
};

const getPostStudyLoad = (
    constraint: Constraint | LineTapConstraint,
    powerAmounts: PowerAmounts
) => {
    const injection_to_use = getInjectionSizeForConstraint(
        constraint,
        powerAmounts
    );

    return (
        (injection_to_use * constraint.dfax) / constraint.rating +
        constraint.preStudyLoad
    );
};

/**
 * Given a ConstraintTableConfig, returns a list of header labels that should be shown for the table.
 */
export const getConstraintsTableHeader = (
    config: ConstraintTableConfig
): string[] => {
    return getConfiguredColumns(CONSTRAINT_TABLE_COLUMNS, config).map(
        (column) => column.header
    );
};

export const getExportConstraintsToCSV = (
    displayName: string,
    maybeConstraints: Loading<ReadonlyArray<Constraint | LineTapConstraint>>,
    config: RenderConstraintTableValueConfig
): (() => Promise<void>) => {
    if (maybeConstraints === "loading") {
        return () => Promise.resolve();
    }
    const sortedConstraints = [...maybeConstraints].sort(
        (a, b) => a.injectionThreshold - b.injectionThreshold
    );
    const exportObjects = constraintToExportObject(sortedConstraints, config);

    return async () =>
        downloadJSONToCSV({
            json: exportObjects,
            filename: `Nira Constraints - ${displayName}.csv`
        });
};

const constraintToExportObject = (
    constraints: (Constraint | LineTapConstraint)[],
    config: RenderConstraintTableValueConfig
): { [header: string]: string }[] => {
    const configuredColumns = getConfiguredColumns(
        CONSTRAINT_TABLE_COLUMNS,
        config.constraintTableConfig
    );
    return constraints.map((constraint) => {
        const result: { [header: string]: string } = {};
        const withinSize = isConstraintWithinSize(
            constraint,
            config.powerAmounts
        );
        configuredColumns.forEach((column) => {
            const header =
                column.getExportHeader !== undefined
                    ? column.getExportHeader(config.constraintTableConfig)
                    : column.header;
            const value =
                column.renderExportValue !== undefined
                    ? column.renderExportValue({
                          ...config,
                          constraint,
                          withinSize
                      })
                    : column.renderValue({
                          ...config,
                          constraint,
                          withinSize
                      });

            result[header] = value;
        });
        return result;
    });
};
